import { useCallback, useEffect, useMemo, useState } from "react"
import {
  clearSession,
  getSession,
  saveSession,
  getLanguage,
  getLanguages,
  saveLanguage,
  saveLanguages
} from "./storage"
import { setUnauthorizedHandler } from "../network/client"
import * as authApi from "../network/auth"
import * as languagesApi from "../network/languages"
import { clearOfflineData } from "../offline/clearOfflineData"
import { SessionContext } from "./session"
import { resumePendingSummaries } from "../van/summaryPoller"

export function SessionProvider({ children }) {
  const [session, setSession] = useState(() => getSession())
  const [languages, setLanguages] = useState(() => getLanguages())
  const [language, setLanguageState] = useState(() => getLanguage())

  const signOutLocally = useCallback(() => {
    clearSession()
    // A shared iPad must not hand the next person the last one's menu or queued flags.
    clearOfflineData()
    setSession(null)
  }, [])

  // Resume VAN summaries a reload or closed tab left unsaved.
  const accessToken = session?.tokens?.access
  const userId = session?.user?.id
  useEffect(() => {
    if (accessToken) resumePendingSummaries(userId)
  }, [accessToken, userId])

  useEffect(() => {
    setUnauthorizedHandler(signOutLocally)
    return () => setUnauthorizedHandler(null)
  }, [signOutLocally])

  const signIn = useCallback(async ({ email, password }) => {
    const data = await authApi.login({ email, password })
    const next = {
      tokens: data?.tokens ?? null,
      user: {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        role: data?.role,
        isRestaurantAdmin: Boolean(data?.is_restaurant_admin)
      },
      // Single-restaurant users skip the picker; the API hands the restaurant back inline.
      restaurant: data?.multi_restaurant ? null : normalizeRestaurant(data?.restaurant_data)
    }
    saveSession(next)
    setSession(next)
    return next
  }, [])

  const selectRestaurant = useCallback((restaurant) => {
    setSession((current) => {
      if (!current) return current
      const next = { ...current, restaurant: normalizeRestaurant(restaurant) }
      saveSession(next)
      return next
    })
  }, [])

  const setLanguage = useCallback((code) => {
    setLanguageState((current) => {
      if (current === code) return current
      saveLanguage(code)
      return code
    })
  }, [])

  // The catalogue is unauthenticated, so it loads before login and keeps the picker
  // honest offline: a failed fetch leaves the last cached list (or the bundled one).
  useEffect(() => {
    const controller = new AbortController()
    languagesApi
      .fetchLanguages({ signal: controller.signal })
      .then((response) => {
        const next = saveLanguages(response)
        setLanguages(next)
        // A deactivated language falls back to the default; a locale that only just
        // became selectable is picked up. Either way content refetches.
        setLanguage(getLanguage(next))
      })
      .catch(() => {})
    return () => controller.abort()
  }, [setLanguage])

  // Drives hyphenation and the browser's CJK font fallback for Japanese content.
  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  const signOut = signOutLocally

  const value = useMemo(
    () => ({
      session,
      user: session?.user ?? null,
      restaurant: session?.restaurant ?? null,
      isAuthenticated: Boolean(session?.tokens?.access),
      language,
      languages,
      setLanguage,
      signIn,
      signOut,
      selectRestaurant
    }),
    [session, language, languages, setLanguage, signIn, signOut, selectRestaurant]
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}


function normalizeRestaurant(restaurant) {
  if (!restaurant?.id) return null
  return {
    id: restaurant.id,
    name: restaurant.name ?? "",
    location: restaurant.location ?? "",
    icon: restaurant.icon ?? ""
  }
}
